import Dexie, { type EntityTable } from 'dexie'
if (import.meta.client) {
  await import('dexie-observable')
}
import { defineStore } from 'pinia'

export interface WorkspaceEntry {
  id: string
  sort?: number
  name?: string
  description?: string
  onlineId?: string
  onlineKey?: string
  lastUpdated?: number
  schemaVersion?: number
  expire?: number
}

export type WorkspaceEventListener = () => void

const workspaceSchemaVersion = 1

export const useWorkspaceStore = defineStore(
  'workspaces',
  () => {

    const {
      createPersonalWorkspace,
      deletePersonalWorkspace,
      getPersonalWorkspaceCurations,
      postCurationsToPersonalWorkspace,
    } = useWorkspace()
    const {
      deleteCuration,
      getCuration,
      listGalleries,
      updateGallery,
      addGallery,
    } = useCurationStore()

    const listeners: WorkspaceEventListener[] = []

    const addListener = (l: WorkspaceEventListener) => {
      listeners.push(l)
    }

    const removeListener = (l: WorkspaceEventListener) => {
      removeFromArray(listeners, l)
    }

    if (import.meta.client) {
      const db = new Dexie('jps-workspace-database') as Dexie & {
        workspaces: EntityTable<WorkspaceEntry, 'id'>
      }
      db.version(1).stores({
        workspaces: '&id, sort',
      })
      db.on('changes', async (changes) => {
        listeners.forEach((f) => f())
      })

      const getAll = async () => {
        return db.workspaces.orderBy('sort').toArray()
      }

      const get = async (id: string) => {
        return db.workspaces.get(id)
      }

      const sort = async (ids: string[]) => {
        ids.forEach((id, index) => {
          updateWorkspace({id, sort: index})
        })
      }

      const updateWorkspace = async (ws: WorkspaceEntry) => {
        await db.workspaces.update(ws.id, ws)
        return db.workspaces.get(ws.id)
      }

      const addWorkspace = async (name: string, description: string = ''): Promise<string> => {
        let total = await db.workspaces.count()
        let ws: WorkspaceEntry = {
          id: hashId(),
          name: name,
          sort: total,
          description: description,
          onlineId: null,
          onlineKey: null,
          lastUpdated: now(),
          expire: null,
          schemaVersion: workspaceSchemaVersion,
        }
        return db.workspaces.add(ws) as unknown as Promise<string>
      }

      const makeOnline = async (id: string, passowrd: string): Promise<WorkspaceEntry> => {
        let we = await db.workspaces.get(id)

        //オンラインWSを作成
        let onlineWorkspace = await createPersonalWorkspace({
          name: {ja: we.name},
          description: {ja: we.description},
          keyHash: passowrd,
        })

        //IDを記録
        we = await updateWorkspace({
          id,
          onlineId: onlineWorkspace.id,
          onlineKey: onlineWorkspace.superKeyHash,
          expire: onlineWorkspace.expired,
          lastUpdated: now(),
        })

        //Workspace配下のCurationをアップロード
        const entries = await listGalleries(we.id)
        if (entries.length) {
          const curations = []
          for (let entry of entries) {
            let cue = await getCuration(entry.id)
            curations.push(cue.c)
          }
          await postCurationsToPersonalWorkspace(we.onlineId, we.onlineKey, curations)
        }

        return we
      }

      const syncOnlineCurations = async (id: string) => {
        let we = await db.workspaces.get(id)
        if (!we.onlineId) throw new Error('オンラインではないワークスペース')
        try {
          let curations = await getPersonalWorkspaceCurations(
            we.onlineId,
            we.onlineKey
          )
          for (const c of curations) {
            c.id = c.id.split('-')[1].split('_')[1]
            let inDb = await getCuration(c.id)
            if (inDb) {
              await updateGallery(c, inDb.sort, id)
            } else {
              await addGallery(c, curations.length, id)
            }
          }
          //同期後、もし期限切れなら、オフラインにする。
          if (we.expire < now()) {
            await makeOffline(id, we)
          }
        } catch (e) {
        }
      }

      const makeOffline = async (id: string, we: any = null) => {
        let _we = null
        if (we) {
          _we = we
        } else {
          _we = await db.workspaces.get(id)
          //同期
          try {
            await syncOnlineCurations(id)
          } catch (e) {
            console.info(e)
          }
        }
        //オンライン個人ワークスペースを削除
        try {
          await deletePersonalWorkspace(_we.onlineId, _we.onlineKey)
        } catch (e) {
          console.error(e)
        }
        //オンラインの削除に成功しても失敗してもこちらはオフラインに。サーバ側はいずれ消えるので。
        _we = await updateWorkspace({
          id: id,
          onlineId: null,
          onlineKey: null,
          expire: null,
          lastUpdated: now(),
        })
        return _we
      }

      const deleteWorkspace = async (id: string) => {
        let we = await db.workspaces.get(id)
        if (we.onlineId) {
          await deletePersonalWorkspace(we.onlineId, we.onlineKey)
        }
        //配下のギャラリーを削除
        for (const g of (await listGalleries(we.id))) {
          await deleteCuration(g.id)
        }
        await db.workspaces.delete(id)
      }

      return {
        addListener,
        removeListener,
        getAll,
        get,
        sort,
        updateWorkspace,
        addWorkspace,
        makeOnline,
        syncOnlineCurations,
        makeOffline,
        deleteWorkspace,
      }

    } else {

      const getAll = async () => { return [] }
      const get = async (id: string) => { return null }
      const sort = async (ids: string[]) => {}
      const updateWorkspace = async (ws: WorkspaceEntry) => { return null }
      const addWorkspace = async (name: string, description: string = ''): Promise<string> => { return null }
      const makeOnline = async (id: string, passowrd: string): Promise<WorkspaceEntry> => { return null }
      const syncOnlineCurations = async (id: string) => {}
      const makeOffline = async (id: string) => { return null }
      const deleteWorkspace = async (id: string) => {}

      return {
        addListener,
        removeListener,
        getAll,
        get,
        sort,
        updateWorkspace,
        addWorkspace,
        makeOnline,
        syncOnlineCurations,
        makeOffline,
        deleteWorkspace,
      }

    }

  },
  { persist: false },
)
